// ============================================================================
// LandingPage v3 — Brand-aligned (cyan/purple), 2026 viral design
// Featuring: 3 verifiable moats (pre-trade veto, counterfactual tracking, zero broker access)
// proof, TDI score, Affiliate program. Single source of truth.
// ============================================================================
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import { supabase } from '@/integrations/supabase/client';
import logoSrc from '@/assets/snap-chart-logo-final.png';
import SafeIcon from '../components/SafeIcon';
import LiveActivityTicker from '../components/LiveActivityTicker';

const {
  FiArrowRight, FiCheck, FiZap, FiTarget, FiCpu, FiCamera, FiBook,
  FiTrendingUp, FiShield, FiActivity, FiDollarSign, FiAward, FiUsers,
  FiPlay, FiCopy, FiChevronRight, FiAlertCircle, FiMenu, FiX,
} = FiIcons;

const SEED_STATS = { analyses: 894, vetoes: 179, traders: 108, capital: 47820 };

function useCountUp(target, durationMs = 1500) {
  const [v, setV] = useState(0);
  useEffect(() => {
    let start; let raf;
    const tick = (ts) => {
      if (!start) start = ts;
      const p = Math.min(1, (ts - start) / durationMs);
      setV(Math.round(target * (1 - Math.pow(1 - p, 3))));
      if (p < 1) raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [target, durationMs]);
  return v;
}

const VERDICTS = [
  { asset: 'XAUUSD', price: '$4,142', verdict: 'NO-TRADE', conf: 60, tone: 'purple' },
  { asset: 'BTCUSD', price: '$63,210', verdict: 'WAIT',    conf: 55, tone: 'amber' },
  { asset: 'NAS100', price: '$20,180', verdict: 'TRADE',   conf: 72, tone: 'emerald' },
  { asset: 'ETHUSD', price: '$2,890',  verdict: 'NO-TRADE', conf: 65, tone: 'purple' },
];
const TONE = {
  amber:   { text: 'text-amber-400',   bg: 'bg-amber-500/10',   border: 'border-amber-500/30',   ring: 'shadow-amber-500/30',   dot: 'bg-amber-400' },
  purple:  { text: 'text-purple-400',  bg: 'bg-purple-500/10',  border: 'border-purple-500/30',  ring: 'shadow-purple-500/30',  dot: 'bg-purple-400' },
  emerald: { text: 'text-emerald-400', bg: 'bg-emerald-500/10', border: 'border-emerald-500/30', ring: 'shadow-emerald-500/30', dot: 'bg-emerald-400' },
};

const LandingPage = () => {
  const [stats, setStats] = useState(SEED_STATS);
  useEffect(() => {
    supabase.rpc('get_public_landing_stats').then(({ data }) => {
      if (data && typeof data === 'object') setStats((s) => ({ ...s, ...data }));
    }, () => {});
  }, []);
  const analysesC = useCountUp(stats.analyses);
  const vetoesC   = useCountUp(stats.vetoes);
  const tradersC  = useCountUp(stats.traders);
  const capitalC  = useCountUp(stats.capital);

  const [tIdx, setTIdx] = useState(0);
  useEffect(() => {
    const id = setInterval(() => setTIdx((i) => (i + 1) % VERDICTS.length), 1800);
    return () => clearInterval(id);
  }, []);
  const v = VERDICTS[tIdx]; const t = TONE[v.tone];

  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  // Lock body scroll while mobile menu is open
  useEffect(() => {
    document.body.style.overflow = mobileMenuOpen ? 'hidden' : '';
    return () => { document.body.style.overflow = ''; };
  }, [mobileMenuOpen]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-900 to-black text-white overflow-x-hidden">

      {/* ═══════════ SLIM 2026 NAVBAR ═══════════ */}
      <nav className="fixed top-0 left-0 right-0 z-50 backdrop-blur-xl bg-gray-900/80 border-b border-white/5">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-14 flex items-center justify-between">
          {/* Logo + name */}
          <Link to="/" className="flex items-center gap-2.5 group min-w-0">
            <img src={logoSrc} alt="SnapChart AI" className="h-9 w-9 sm:h-10 sm:w-10 flex-shrink-0 transition group-hover:scale-110" />
            <span className="font-bold text-sm sm:text-base md:text-lg tracking-tight truncate">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">SnapChart AI</span>
            </span>
          </Link>

          {/* Center nav — desktop only */}
          <div className="hidden md:flex items-center gap-7 text-xs font-mono uppercase tracking-[0.15em] text-gray-400">
            <Link to="/test" className="text-amber-300 hover:text-amber-200 transition">
              <span className="mr-1.5">▸</span>free test
            </Link>
            <a href="#how" className="hover:text-cyan-400 transition">how</a>
            <a href="#pricing" className="hover:text-cyan-400 transition">pricing</a>
            <Link to="/affiliate" className="hover:text-cyan-400 transition">affiliate</Link>
          </div>

          {/* Right CTA + mobile hamburger */}
          <div className="flex items-center gap-2">
            <Link to="/login" className="hidden sm:inline text-xs font-mono uppercase tracking-[0.15em] text-gray-400 hover:text-cyan-400 transition px-2 py-1.5">
              log in
            </Link>
            <Link to="/register"
              className="inline-flex px-3 sm:px-4 py-2 rounded-lg bg-gradient-to-r from-purple-500/20 to-cyan-500/20 border border-cyan-400/30 text-cyan-300 text-xs font-bold hover:border-cyan-400/60 hover:from-purple-500/30 hover:to-cyan-500/30 transition items-center gap-1.5 min-h-[36px]">
              <span className="hidden sm:inline">Start free</span><span className="sm:hidden">Sign up</span>
              <SafeIcon icon={FiArrowRight} className="text-[10px]" />
            </Link>
            {/* Mobile hamburger */}
            <button
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open menu"
              className="md:hidden p-2 rounded-lg border border-white/10 text-gray-300 hover:text-cyan-400 hover:border-cyan-400/40 transition min-w-[40px] min-h-[40px] flex items-center justify-center">
              <SafeIcon icon={FiMenu} className="text-lg" />
            </button>
          </div>
        </div>
      </nav>

      {/* ═══════════ MOBILE MENU DRAWER ═══════════ */}
      {mobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
          className="fixed inset-0 z-[60] md:hidden bg-gray-950/95 backdrop-blur-xl flex flex-col"
        >
          {/* Mobile menu header */}
          <div className="flex items-center justify-between p-4 border-b border-white/5">
            <Link to="/" onClick={() => setMobileMenuOpen(false)} className="flex items-center gap-2">
              <img src={logoSrc} alt="" className="h-9 w-9" />
              <span className="font-bold text-base bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                SnapChart AI
              </span>
            </Link>
            <button
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close menu"
              className="p-2 rounded-lg border border-white/10 text-gray-300 hover:text-cyan-400 hover:border-cyan-400/40 transition min-w-[44px] min-h-[44px] flex items-center justify-center">
              <SafeIcon icon={FiX} className="text-xl" />
            </button>
          </div>

          {/* Mobile menu nav links — big touch targets */}
          <nav className="flex-1 overflow-y-auto p-6 space-y-2">
            <Link to="/test" onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 p-4 rounded-xl border border-amber-400/30 bg-amber-500/5 hover:bg-amber-500/10 transition">
              <span className="text-2xl">▸</span>
              <div className="flex-1">
                <div className="font-bold text-amber-300 text-base">Free test</div>
                <div className="text-xs text-amber-400/60 font-mono uppercase tracking-wider">how much would you save?</div>
              </div>
              <SafeIcon icon={FiArrowRight} className="text-amber-400" />
            </Link>

            {[
              { label: 'How it works', desc: 'screenshot → verdict in 2s', href: '/#how' },
              { label: 'Pricing', desc: '30-day free trial', href: '/#pricing' },
            ].map((l) => (
              <a key={l.label} href={l.href} onClick={() => setMobileMenuOpen(false)}
                className="flex items-center gap-3 p-4 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/5 transition">
                <div className="flex-1">
                  <div className="font-semibold text-white text-base">{l.label}</div>
                  <div className="text-xs text-gray-500 font-mono uppercase tracking-wider">{l.desc}</div>
                </div>
                <SafeIcon icon={FiArrowRight} className="text-gray-500" />
              </a>
            ))}

            <Link to="/affiliate" onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 p-4 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/5 transition">
              <div className="flex-1">
                <div className="font-semibold text-white text-base">Affiliate program</div>
                <div className="text-xs text-cyan-400 font-mono uppercase tracking-wider">30% recurring</div>
              </div>
              <SafeIcon icon={FiArrowRight} className="text-gray-500" />
            </Link>

            <Link to="/login" onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-3 p-4 rounded-xl border border-white/5 bg-white/[0.02] hover:bg-white/5 transition mt-6">
              <div className="flex-1">
                <div className="font-semibold text-gray-300 text-base">Log in</div>
              </div>
              <SafeIcon icon={FiArrowRight} className="text-gray-500" />
            </Link>
          </nav>

          {/* Mobile menu CTA pinned bottom */}
          <div className="p-6 border-t border-white/5">
            <Link to="/register" onClick={() => setMobileMenuOpen(false)}
              className="w-full inline-flex items-center justify-center gap-2 px-5 py-4 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black font-bold text-base shadow-xl shadow-cyan-500/20">
              Start free 30-day trial <SafeIcon icon={FiArrowRight} />
            </Link>
            <p className="text-xs text-gray-500 text-center mt-2 font-mono">no credit card · cancel anytime</p>
          </div>
        </motion.div>
      )}

      {/* ═══════════ HERO ═══════════ */}
      <section className="relative pt-20 md:pt-24 pb-10 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 -left-32 w-[600px] h-[600px] bg-purple-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob" />
          <div className="absolute top-20 -right-32 w-[600px] h-[600px] bg-cyan-500/30 rounded-full mix-blend-multiply filter blur-3xl opacity-50 animate-blob animation-delay-2000" />
          <div className="absolute bottom-0 left-1/3 w-[500px] h-[500px] bg-pink-500/15 rounded-full mix-blend-multiply filter blur-3xl opacity-40 animate-blob animation-delay-4000" />
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#4f4f4f12_1px,transparent_1px),linear-gradient(to_bottom,#4f4f4f12_1px,transparent_1px)] bg-[size:64px_64px]" />
        </div>

        <div className="relative max-w-7xl mx-auto">
          <motion.div initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-gradient-to-r from-purple-500/10 to-cyan-500/10 border border-purple-500/20 mb-8">
            <span className="relative flex h-2 w-2">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-cyan-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-cyan-500" />
            </span>
            <span className="text-sm font-medium bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
              The AI that tells you when NOT to trade.
            </span>
          </motion.div>

          <div className="grid grid-cols-12 gap-6 items-start">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
              className="col-span-12 lg:col-span-7">
              <h1 className="text-4xl sm:text-5xl md:text-7xl lg:text-8xl font-black mb-6 leading-[0.95] tracking-tight">
                <span className="bg-gradient-to-r from-white via-cyan-100 to-purple-200 bg-clip-text text-transparent">Screenshot</span><br />
                <span className="text-white">a chart.</span><br />
                <span className="bg-gradient-to-r from-purple-400 via-cyan-300 to-cyan-400 bg-clip-text text-transparent">Get a verdict in 2s.</span>
              </h1>

              <p className="text-base sm:text-lg md:text-xl text-gray-400 leading-relaxed max-w-2xl mb-8">
                <span className="text-white font-semibold">One-click capture inside the app.</span>{' '}
                Or drag any chart from outside — TradingView, MT4, MT5, every crypto exchange.
                <br className="hidden md:block" />
                2 seconds later: <span className="text-emerald-400 font-mono font-bold">TRADE</span> · <span className="text-purple-400 font-mono font-bold">NO-TRADE</span> · <span className="text-amber-400 font-mono font-bold">WAIT</span>.
                The first AI that vetoes your worst trades <span className="text-cyan-400 font-semibold">before</span> you take them.
              </p>

              <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3 mb-4">
                <Link to="/register"
                  className="group inline-flex items-center justify-center gap-3 px-8 py-4 bg-gradient-to-r from-purple-600/30 to-cyan-600/30 border border-purple-500/40 rounded-xl font-bold text-lg text-cyan-400 hover:from-purple-600/50 hover:to-cyan-600/50 hover:border-cyan-400/60 hover:scale-105 transition-all shadow-xl shadow-cyan-500/20">
                  Start free 30-day trial <SafeIcon icon={FiArrowRight} className="group-hover:translate-x-1 transition" />
                </Link>
                <a href="#how" className="inline-flex items-center justify-center gap-2 px-6 py-4 rounded-xl border border-white/10 text-gray-300 hover:bg-white/5 transition">
                  <SafeIcon icon={FiPlay} className="text-cyan-400" /> See it work
                </a>
              </div>
              <p className="text-xs text-gray-500 ml-1">No credit card required · Cancel anytime</p>

              <div className="mt-10 pt-6 border-t border-white/5">
                <p className="text-[10px] uppercase tracking-[0.25em] text-gray-600 mb-3">built for every trader, every platform</p>
                <div className="flex flex-wrap gap-x-6 gap-y-2 text-gray-500 font-semibold text-sm">
                  <span className="hover:text-cyan-400 transition">FTMO</span>
                  <span className="hover:text-cyan-400 transition">FundedNext</span>
                  <span className="hover:text-cyan-400 transition">Topstep</span>
                  <span className="hover:text-cyan-400 transition">MT4 · MT5</span>
                  <span className="hover:text-cyan-400 transition">TradingView</span>
                  <span className="hover:text-cyan-400 transition">Binance</span>
                  <span className="hover:text-cyan-400 transition">Bybit</span>
                  <span className="hover:text-cyan-400 transition">Coinbase</span>
                  <span className="hover:text-cyan-400 transition">Kraken</span>
                </div>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
              className="col-span-12 lg:col-span-5">
              <div className="relative">
                <div className="absolute -inset-1 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 rounded-3xl blur-2xl" />
                <div className="relative bg-gray-900/80 backdrop-blur-xl border border-white/10 rounded-3xl p-6 shadow-2xl">
                  <div className="flex items-center justify-between mb-5">
                    <div className="flex items-center gap-2 text-xs text-gray-400">
                      <img src={logoSrc} alt="" className="w-5 h-5" />
                      <span className="font-mono uppercase tracking-wider">snap chart ai</span>
                    </div>
                    <span className="flex items-center gap-1.5 text-[10px] font-mono uppercase tracking-wider text-gray-500">
                      <span className="w-1.5 h-1.5 rounded-full bg-emerald-400 animate-pulse" /> live
                    </span>
                  </div>

                  <motion.div key={tIdx} initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.4 }}
                    className={`rounded-2xl border ${t.border} ${t.bg} p-6 mb-4 shadow-2xl ${t.ring}`}>
                    <div className="flex items-baseline justify-between mb-4">
                      <div>
                        <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1">asset</div>
                        <div className="text-2xl font-bold text-white">${v.asset}</div>
                      </div>
                      <div className="text-right">
                        <div className="text-[10px] font-mono uppercase tracking-wider text-gray-500 mb-1">current</div>
                        <div className="text-xl font-mono text-gray-200">{v.price}</div>
                      </div>
                    </div>
                    <div className={`text-5xl font-black tracking-tight ${t.text} mb-2 font-mono`}>{v.verdict}</div>
                    <div className="flex items-center justify-between text-[11px] font-mono uppercase tracking-wider text-gray-500">
                      <span>{v.conf}% confidence</span>
                      <span>generated · 1.8s</span>
                    </div>
                  </motion.div>

                  <div className="space-y-1.5 font-mono text-xs">
                    {VERDICTS.slice(0, 3).map((vv, i) => {
                      const tt = TONE[vv.tone];
                      return (
                        <div key={i} className="flex items-center justify-between text-gray-500 px-2 py-1 rounded hover:bg-white/[0.02]">
                          <span className="flex items-center gap-2">
                            <span className={`w-1 h-1 rounded-full ${tt.dot}`} />
                            ${vv.asset}
                          </span>
                          <span className="text-gray-600">{vv.price}</span>
                          <span className={tt.text}>{vv.verdict}</span>
                        </div>
                      );
                    })}
                  </div>

                  <Link to="/register" className="block mt-4 text-center text-xs text-cyan-400 hover:text-cyan-300 py-2 border-t border-white/5">
                    Try it now → snap-chart.ai
                  </Link>
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* ═══════════ LIVE ACTIVITY TICKER — social proof real-time ═══════════ */}
      <LiveActivityTicker />

      {/* ═══════════ HOW IT ACTUALLY WORKS — 2 flows ═══════════ */}
      <section className="relative py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-xs font-mono uppercase tracking-[0.2em] text-cyan-400 mb-4">
              two ways. zero friction.
            </span>
            <h2 className="text-3xl md:text-5xl font-black tracking-tight mb-3">
              <span className="text-white">From chart to verdict</span>
              <br />
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">in 2 seconds.</span>
            </h2>
          </div>

          <div className="grid grid-cols-12 gap-4">
            {/* Flow 1 — In-app capture */}
            <div className="col-span-12 md:col-span-6 rounded-3xl border-2 border-cyan-400/40 bg-gradient-to-br from-cyan-500/10 via-gray-900/60 to-purple-500/5 p-6 md:p-8 relative overflow-hidden shadow-2xl shadow-cyan-500/10">
              <div className="absolute -top-3 left-6 px-2.5 py-0.5 rounded-md bg-cyan-400 text-gray-900 text-[10px] font-mono uppercase tracking-[0.15em] font-bold">
                fastest
              </div>
              <div className="flex items-center gap-2 mb-3 mt-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400">flow 01 · inside snapchart</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-white mb-3 leading-tight">
                <span className="bg-gradient-to-r from-cyan-300 to-white bg-clip-text text-transparent">One click.</span> Done.
              </h3>
              <p className="text-sm text-gray-300 leading-relaxed mb-5">
                Open the <span className="text-white font-semibold">built-in TradingView chart</span> inside the app. Press <span className="font-mono px-2 py-0.5 rounded bg-cyan-500/20 border border-cyan-500/30 text-cyan-300">📸 Capture Chart</span>. AI analyzes it via Screen Capture API. Verdict in 2 seconds.
              </p>
              <ul className="space-y-1.5 text-xs font-mono text-gray-400">
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-cyan-400 mt-0.5 flex-shrink-0" /> XAUUSD · BTCUSD · NAS100 · SPX · ETH · SOL · DAX · 10+ assets</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-cyan-400 mt-0.5 flex-shrink-0" /> No download. No broker login. Browser-native.</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-cyan-400 mt-0.5 flex-shrink-0" /> Auto-saved to your journal with timestamp + verdict</li>
              </ul>
            </div>

            {/* Flow 2 — External drag-drop */}
            <div className="col-span-12 md:col-span-6 rounded-3xl border border-white/15 bg-gradient-to-br from-purple-500/5 via-gray-900/60 to-transparent p-6 md:p-8 relative">
              <div className="flex items-center gap-2 mb-3 mt-2">
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-purple-400">flow 02 · from outside</span>
              </div>
              <h3 className="text-2xl md:text-3xl font-black text-white mb-3 leading-tight">
                <span className="bg-gradient-to-r from-purple-300 to-white bg-clip-text text-transparent">Drag.</span> Drop. Done.
              </h3>
              <p className="text-sm text-gray-300 leading-relaxed mb-5">
                You trade somewhere else? <span className="text-white font-semibold">Drag any chart screenshot</span> into the app. Works with MT4, MT5, vanilla TradingView, your broker, any crypto exchange. We never touch them.
              </p>
              <ul className="space-y-1.5 text-xs font-mono text-gray-400">
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-purple-400 mt-0.5 flex-shrink-0" /> Any platform · any timeframe · any asset</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-purple-400 mt-0.5 flex-shrink-0" /> Zero broker connection. Zero credentials shared.</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-purple-400 mt-0.5 flex-shrink-0" /> Same AI engine. Same 2-second verdict.</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ THE MOAT — 3 things nobody else does ═══════════ */}
      <section className="relative py-10 px-4 sm:px-6 lg:px-8">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-500/[0.03] to-transparent pointer-events-none" />
        <div className="relative max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-xs font-mono uppercase tracking-[0.2em] text-cyan-400 mb-6">
              the moat — 3 things nobody else does
            </span>
            <h2 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
              <span className="bg-gradient-to-r from-purple-400 via-cyan-300 to-cyan-400 bg-clip-text text-transparent">
                Why we win.
              </span>
            </h2>
            <p className="text-lg md:text-xl text-gray-400 max-w-3xl mx-auto leading-relaxed">
              Three things no other tool does. <span className="text-white font-semibold">Verifiable.</span>{' '}
              Pick any competitor and check.
            </p>
          </div>

          <div className="grid grid-cols-12 gap-4 mb-10">
            {[
              {
                num: '01',
                title: 'Pre-trade veto',
                tagline: 'We stop you BEFORE the loss',
                body: 'Edgewonk, TraderVue, MyChartSetup — all analyze trades AFTER you took them. We give a verdict BEFORE. Post-trade is therapy. Pre-trade is prevention.',
                color: 'cyan',
                icon: FiTarget,
              },
              {
                num: '02',
                title: 'Counterfactual capital tracking',
                tagline: 'We measure what NOT trading saved you',
                body: 'Every NO-TRADE the AI calls right, we calculate how much capital that decision preserved. In real dollars. Live. No other SaaS in trading does this.',
                color: 'emerald',
                icon: FiDollarSign,
              },
              {
                num: '03',
                title: 'Zero broker connection',
                tagline: 'Your broker never knows we exist',
                body: 'No API key. No IP whitelist. No read-write permissions. Just upload a chart screenshot. Works on TradingView, MT4, MT5, every crypto exchange — because we never touch them.',
                color: 'purple',
                icon: FiShield,
              },
            ].map((b, i) => (
              <motion.div key={b.num}
                initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.08 }}
                className={`col-span-12 md:col-span-4 rounded-2xl border ${b.color === 'cyan' ? 'border-cyan-500/30' : b.color === 'emerald' ? 'border-emerald-500/30' : 'border-purple-500/30'} bg-gradient-to-b from-white/[0.03] to-transparent p-6 hover:scale-[1.02] transition`}>
                <div className="flex items-center justify-between mb-4">
                  <span className={`font-mono text-xs ${b.color === 'cyan' ? 'text-cyan-400' : b.color === 'emerald' ? 'text-emerald-400' : 'text-purple-400'} tracking-wider`}>/{b.num}</span>
                  <SafeIcon icon={b.icon} className={`text-2xl ${b.color === 'cyan' ? 'text-cyan-400' : b.color === 'emerald' ? 'text-emerald-400' : 'text-purple-400'}`} />
                </div>
                <h3 className="text-2xl font-bold text-white mb-2">{b.title}</h3>
                <p className={`text-sm font-semibold mb-3 ${b.color === 'cyan' ? 'text-cyan-300' : b.color === 'emerald' ? 'text-emerald-300' : 'text-purple-300'}`}>{b.tagline}</p>
                <p className="text-sm text-gray-400 leading-relaxed">{b.body}</p>
              </motion.div>
            ))}
          </div>

          {/* Comparison table — Other tools vs Us (desktop: grid, mobile: stacked cards) */}
          <div className="rounded-2xl border-2 border-cyan-400/30 bg-gradient-to-br from-cyan-500/5 via-transparent to-purple-500/5 p-5 md:p-8 shadow-2xl shadow-cyan-500/10">
            {/* DESKTOP — 3-column grid */}
            <div className="hidden md:grid grid-cols-12 gap-6 text-sm">
              <div className="col-span-4 font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 pb-3 border-b border-white/10"></div>
              <div className="col-span-4 font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 pb-3 border-b border-white/10 text-center">Other tools</div>
              <div className="col-span-4 font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 pb-3 border-b border-cyan-400/30 text-center">SnapChart AI</div>

              {[
                ['Broker access',     'Required (risky)', 'None ever'],
                ['Timing',            'Post-trade journal', 'Pre-trade veto'],
                ['Capital metric',    'Win rate %', '$ preserved (real)'],
                ['Setup time',        '24h+ support tickets', '30 seconds'],
                ['Works without API', '❌', '✅ Always'],
              ].map(([label, them, us], i) => (
                <React.Fragment key={i}>
                  <div className="col-span-4 py-3 text-white font-medium border-b border-white/5">{label}</div>
                  <div className="col-span-4 py-3 text-gray-500 text-center border-b border-white/5 line-through">{them}</div>
                  <div className="col-span-4 py-3 text-cyan-300 font-semibold text-center border-b border-cyan-400/10">{us}</div>
                </React.Fragment>
              ))}
            </div>
            {/* MOBILE — stacked rows: label on top, then 2 columns under */}
            <div className="md:hidden space-y-3">
              {[
                ['Broker access',     'Required (risky)', 'None ever'],
                ['Timing',            'Post-trade journal', 'Pre-trade veto'],
                ['Capital metric',    'Win rate %', '$ preserved (real)'],
                ['Setup time',        '24h+ support tickets', '30 seconds'],
                ['Works without API', 'No', 'Yes · always'],
              ].map(([label, them, us], i) => (
                <div key={i} className="rounded-xl border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500 mb-2">{label}</div>
                  <div className="grid grid-cols-2 gap-2 text-xs">
                    <div className="p-2 rounded-lg bg-white/[0.02]">
                      <div className="font-mono text-[9px] uppercase tracking-wider text-gray-600 mb-0.5">others</div>
                      <div className="text-gray-500 line-through leading-snug">{them}</div>
                    </div>
                    <div className="p-2 rounded-lg bg-cyan-500/5 border border-cyan-400/20">
                      <div className="font-mono text-[9px] uppercase tracking-wider text-cyan-400 mb-0.5">us</div>
                      <div className="text-cyan-300 font-semibold leading-snug">{us}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ FOUNDER NOTE — The story that makes it real ═══════════ */}
      <section className="relative py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="relative rounded-3xl border border-white/10 bg-gradient-to-br from-purple-900/10 via-gray-900/50 to-cyan-900/10 p-8 md:p-12 overflow-hidden">
            <div className="absolute top-0 right-0 w-64 h-64 bg-purple-500/10 rounded-full blur-3xl" />
            <div className="relative">
              <span className="inline-block px-3 py-1 rounded-full bg-white/[0.04] border border-white/10 text-[10px] font-mono uppercase tracking-[0.2em] text-gray-400 mb-5">
                a note from the founder
              </span>
              <blockquote className="text-xl md:text-3xl font-bold text-white leading-snug mb-6">
                "I blew my first prop firm account in 11 days because I couldn't say <span className="text-amber-400 italic">NO</span> to a setup that looked perfect.
                <br className="hidden md:block" />
                <br className="hidden md:block" />
                So I built the thing that <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">would have said it for me.</span>"
              </blockquote>
              <div className="flex items-center gap-3 pt-4 border-t border-white/5">
                <div className="w-10 h-10 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-sm font-bold text-gray-900">
                  JM
                </div>
                <div>
                  <div className="text-sm font-semibold text-white">Jean-Michel</div>
                  <div className="text-xs text-gray-500 font-mono">founder, snapchart ai · building in public</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ WHAT WE'RE NOT — Anti-marketing viral ═══════════ */}
      <section className="relative py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-amber-500/10 border border-amber-500/20 text-xs font-mono uppercase tracking-[0.2em] text-amber-300 mb-4">
              honesty over hype
            </span>
            <h2 className="text-3xl md:text-5xl font-black mb-3">
              <span className="text-white">What we're</span> <span className="text-amber-400 italic">NOT</span>.
            </h2>
          </div>

          <div className="space-y-3 max-w-3xl mx-auto">
            {[
              { not: "A signal bot.", we: "We tell you when NOT to trade." },
              { not: "A chart pattern detector.", we: "We read structure + regime + risk, not patterns." },
              { not: "Another trading journal.", we: "Journals analyze AFTER. We veto BEFORE." },
              { not: "A guarantee of profit.", we: "We're discipline. Profit is your job." },
              { not: "A broker integration.", we: "We never touch your account. Ever." },
            ].map((row, i) => (
              <motion.div key={i}
                initial={{ opacity: 0, x: -10 }} whileInView={{ opacity: 1, x: 0 }} viewport={{ once: true }} transition={{ delay: i * 0.05 }}
                className="flex flex-col md:flex-row items-start md:items-center gap-3 md:gap-6 p-4 rounded-xl border border-white/5 bg-white/[0.02]">
                <div className="flex items-center gap-2 md:w-1/2">
                  <span className="text-2xl text-amber-400/60">✗</span>
                  <span className="text-gray-400 line-through text-lg">{row.not}</span>
                </div>
                <div className="flex items-center gap-2 md:w-1/2 md:border-l md:border-white/5 md:pl-6">
                  <span className="text-2xl text-cyan-400">→</span>
                  <span className="text-white font-medium">{row.we}</span>
                </div>
              </motion.div>
            ))}
          </div>

          <p className="text-center text-sm text-gray-500 mt-8 max-w-2xl mx-auto leading-relaxed">
            We're the <span className="text-white font-semibold">brakes</span>. Not the gas. Most traders blow up because nobody told them to STOP. <span className="text-cyan-400">We do.</span>
          </p>
        </div>
      </section>

      {/* ═══════════ VISION — Category creator ═══════════ */}
      <section className="relative py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="relative rounded-3xl border border-purple-500/30 bg-gradient-to-br from-purple-900/15 via-gray-900/80 to-cyan-900/15 p-8 md:p-12 overflow-hidden">
            <div className="absolute -top-32 -right-32 w-96 h-96 bg-purple-500/15 rounded-full blur-3xl" />
            <div className="absolute -bottom-32 -left-32 w-96 h-96 bg-cyan-500/15 rounded-full blur-3xl" />
            <div className="relative">
              <span className="inline-block px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-[10px] font-mono uppercase tracking-[0.2em] text-purple-300 mb-4">
                the bigger picture
              </span>
              <h2 className="text-3xl md:text-5xl font-black mb-5 leading-tight">
                <span className="text-white">We're not building a trading tool.</span><br />
                <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                  We're building a category.
                </span>
              </h2>
              <p className="text-base md:text-lg text-gray-300 leading-relaxed mb-6 max-w-3xl">
                Every other AI in trading is built to make traders trade <span className="italic">more</span>. We're the only one that gets paid more when our users trade <span className="text-white font-semibold">less</span>. Aligned incentives. New category.{' '}
                <span className="text-cyan-400 font-semibold">The AI Risk Officer.</span>
              </p>
              <div className="grid grid-cols-12 gap-3 text-sm">
                <div className="col-span-12 md:col-span-4 p-4 rounded-xl border border-white/10 bg-black/30">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-purple-300 mb-1">Year 1</div>
                  <div className="text-white font-semibold">For retail prop traders</div>
                </div>
                <div className="col-span-12 md:col-span-4 p-4 rounded-xl border border-white/10 bg-black/30">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-cyan-300 mb-1">Year 3</div>
                  <div className="text-white font-semibold">Required by prop firms (TDI = FICO for traders)</div>
                </div>
                <div className="col-span-12 md:col-span-4 p-4 rounded-xl border border-white/10 bg-black/30">
                  <div className="font-mono text-[10px] uppercase tracking-wider text-emerald-300 mb-1">Year 5</div>
                  <div className="text-white font-semibold">Standard layer in every broker stack</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ PRODUCT TOUR — Bento ═══════════ */}
      <section id="how" className="relative py-10 px-4 sm:px-6 lg:px-8 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-8">
            <span className="inline-block px-3 py-1 rounded-full bg-purple-500/10 border border-purple-500/20 text-xs font-mono uppercase tracking-[0.2em] text-purple-400 mb-6">
              the full stack
            </span>
            <h2 className="text-4xl md:text-6xl font-black mb-4">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">
                Discipline, automated.
              </span>
            </h2>
            <p className="text-lg text-gray-400 max-w-2xl mx-auto">
              Not just verdicts. A complete trading discipline engine.
            </p>
          </div>

          <div className="grid grid-cols-12 gap-4">
            <div className="col-span-12 lg:col-span-7 rounded-3xl border border-emerald-500/30 bg-gradient-to-br from-emerald-900/20 via-gray-900/80 to-cyan-900/20 p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-64 h-64 bg-emerald-500/20 rounded-full blur-3xl opacity-50" />
              <div className="relative">
                <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-emerald-400 mb-4">
                  <SafeIcon icon={FiShield} className="w-4 h-4" />
                  capital preserved by ai
                </div>
                <div className="text-6xl md:text-7xl font-black text-emerald-400 font-mono mb-4 tabular-nums">
                  +${capitalC.toLocaleString()}
                </div>
                <p className="text-gray-300 leading-relaxed max-w-md mb-4">
                  Every NO-TRADE the AI calls right, we calculate how much capital that decision saved.
                  <span className="text-white font-semibold"> Track your discipline ROI in real dollars.</span>
                </p>
                <div className="font-mono text-xs text-gray-500 uppercase tracking-wider">
                  Across {tradersC} traders · {vetoesC} vetoes issued
                </div>
              </div>
            </div>

            <div className="col-span-12 md:col-span-6 lg:col-span-5 rounded-3xl border border-cyan-500/20 bg-gradient-to-br from-cyan-900/15 to-gray-900/80 p-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-cyan-400 mb-4">
                <SafeIcon icon={FiAward} className="w-4 h-4" />
                trader discipline index
              </div>
              <div className="flex items-baseline gap-3 mb-4">
                <span className="text-5xl font-black text-white font-mono">87</span>
                <span className="text-2xl text-gray-500 font-mono">/100</span>
              </div>
              <div className="w-full bg-white/5 rounded-full h-2 mb-4 overflow-hidden">
                <div className="h-full bg-gradient-to-r from-cyan-500 to-emerald-500 rounded-full" style={{ width: '87%' }} />
              </div>
              <p className="text-sm text-gray-400 leading-relaxed">
                FICO-style score that grades your trading discipline. Every NO-TRADE you respect raises it. Every impulse trade lowers it.
              </p>
            </div>

            <div className="col-span-12 md:col-span-6 rounded-3xl border border-white/10 bg-gradient-to-b from-white/[0.03] to-gray-900/80 p-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-purple-400 mb-4">
                <SafeIcon icon={FiBook} className="w-4 h-4" />
                trading journal — auto
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">Every analysis stored. Every verdict tracked.</h3>
              <p className="text-sm text-gray-400 leading-relaxed mb-4">
                Auto-saves every chart you analyze. Build a searchable history of your verdicts, your outcomes, and your discipline patterns over time.
              </p>
              <div className="flex flex-wrap gap-2 font-mono text-[10px] uppercase tracking-wider">
                <span className="px-2 py-1 rounded bg-white/5 text-gray-400">filter by asset</span>
                <span className="px-2 py-1 rounded bg-white/5 text-gray-400">filter by verdict</span>
                <span className="px-2 py-1 rounded bg-white/5 text-gray-400">export csv</span>
              </div>
            </div>

            <div className="col-span-12 md:col-span-6 rounded-3xl border border-amber-500/20 bg-gradient-to-br from-amber-900/15 via-gray-900/80 to-orange-900/10 p-8">
              <div className="flex items-center gap-2 text-xs uppercase tracking-wider text-amber-400 mb-4">
                <SafeIcon icon={FiTrendingUp} className="w-4 h-4" />
                counterfactual capital tracking
              </div>
              <h3 className="text-2xl font-bold text-white mb-3">"What if I had taken that trade?"</h3>
              <p className="text-sm text-gray-400 leading-relaxed">
                Every NO-TRADE verdict is tracked for 24h. We calculate exactly what your discipline saved (or cost) you — in R-multiples and dollars. <span className="text-white font-semibold">No other tool does this.</span>
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ LIVE PROOF WALL ═══════════ */}
      <section className="relative py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-5xl font-black mb-3">
              <span className="bg-gradient-to-r from-purple-400 to-cyan-400 bg-clip-text text-transparent">Live numbers.</span>
              {' '}<span className="text-white/40">Receipts only.</span>
            </h2>
            <p className="text-gray-400">Updated from the production database. No vanity metrics.</p>
          </div>

          <div className="grid grid-cols-12 gap-3 md:gap-4">
            {[
              { label: 'analyses run', value: analysesC.toLocaleString(), color: 'text-white' },
              { label: 'no-trade vetoes', value: vetoesC.toLocaleString(), color: 'text-purple-400' },
              { label: 'capital preserved', value: `$${capitalC.toLocaleString()}`, color: 'text-emerald-400' },
              { label: 'active traders', value: tradersC.toLocaleString(), color: 'text-cyan-400' },
            ].map((s) => (
              <div key={s.label} className="col-span-6 md:col-span-3 rounded-2xl border border-white/10 bg-white/[0.02] p-6">
                <span className="text-[10px] font-mono uppercase tracking-[0.2em] text-gray-500 block mb-3">{s.label}</span>
                <div className={`text-3xl md:text-4xl font-black font-mono tabular-nums ${s.color}`}>{s.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ═══════════ AFFILIATE ═══════════ */}
      <section className="relative py-8 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="relative rounded-3xl border border-amber-500/30 bg-gradient-to-br from-amber-900/20 via-gray-900/60 to-emerald-900/20 p-8 md:p-12 overflow-hidden">
            <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,rgba(245,158,11,0.15),transparent_60%)] pointer-events-none" />
            <div className="relative grid grid-cols-12 gap-6 items-center">
              <div className="col-span-12 lg:col-span-8">
                <span className="inline-block px-3 py-1 rounded-full bg-amber-500/15 border border-amber-500/30 text-[10px] font-mono uppercase tracking-[0.2em] text-amber-300 mb-4">
                  30% recurring · paid monthly
                </span>
                <h2 className="text-3xl md:text-5xl font-black text-white mb-3 leading-tight">
                  Trader with an audience?
                  <br />
                  <span className="bg-gradient-to-r from-amber-400 to-emerald-400 bg-clip-text text-transparent">Earn 30% recurring forever.</span>
                </h2>
                <p className="text-gray-300 leading-relaxed max-w-2xl">
                  On every subscription you refer. Paid monthly in USDT or PayPal. No cap, no expiry. Built for traders with following on Twitter, YouTube, TikTok, Telegram.
                </p>
              </div>
              <div className="col-span-12 lg:col-span-4">
                <Link to="/affiliate"
                  className="block text-center px-6 py-4 rounded-xl bg-gradient-to-r from-amber-500 to-emerald-500 text-black font-bold hover:scale-105 transition shadow-2xl shadow-amber-500/30">
                  Join Affiliate Program →
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ PRICING ═══════════ */}
      <section id="pricing" className="relative py-10 px-4 sm:px-6 lg:px-8 scroll-mt-20">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <span className="inline-block px-3 py-1 rounded-full bg-cyan-500/10 border border-cyan-500/20 text-xs font-mono uppercase tracking-[0.2em] text-cyan-400 mb-6">pricing</span>
            <h2 className="text-4xl md:text-6xl font-black mb-3">
              <span className="text-white">Pick your level.</span>
            </h2>
            <p className="text-lg text-gray-400">30-day free trial. No card. Cancel anytime.</p>
          </div>

          <div className="grid grid-cols-12 gap-4 md:gap-5 items-end">
            <div className="col-span-12 md:col-span-4 rounded-3xl border border-white/10 bg-gray-900/60 p-8 flex flex-col">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-gray-500 mb-3">Free forever</span>
              <h3 className="text-2xl font-bold text-white mb-4">Starter</h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-5xl font-black font-mono text-white">$0</span>
                <span className="text-gray-500 text-sm">/forever</span>
              </div>
              <ul className="space-y-2.5 text-sm text-gray-400 flex-grow mb-6">
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-gray-500 mt-0.5 flex-shrink-0" /> 5 analyses / month</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-gray-500 mt-0.5 flex-shrink-0" /> All asset classes</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-gray-500 mt-0.5 flex-shrink-0" /> Basic verdict</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-gray-500 mt-0.5 flex-shrink-0" /> Multi-chart supported (within 5/mo)</li>
              </ul>
              <Link to="/register" className="inline-flex justify-center px-4 py-3 rounded-xl border border-white/15 text-gray-200 hover:bg-white/5 transition text-sm font-medium">
                Start free
              </Link>
            </div>

            <div className="col-span-12 md:col-span-4 rounded-3xl border-2 border-cyan-400/50 bg-gradient-to-br from-cyan-500/10 via-gray-900/80 to-purple-500/10 p-8 flex flex-col relative shadow-2xl shadow-cyan-500/20">
              <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full bg-gradient-to-r from-cyan-400 to-purple-400 text-gray-900 text-[10px] font-mono uppercase tracking-[0.15em] font-bold">
                most-picked
              </div>
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-cyan-400 mb-3">Built for traders</span>
              <h3 className="text-2xl font-bold text-white mb-4">Pro Trader</h3>
              <div className="flex items-baseline gap-1 mb-1">
                <span className="text-5xl font-black font-mono bg-gradient-to-r from-cyan-400 to-purple-400 bg-clip-text text-transparent">$49</span>
                <span className="text-gray-500 text-sm">/month</span>
              </div>
              <p className="text-xs text-gray-500 mb-6 font-mono">$290/year · 2 months free</p>
              <ul className="space-y-2.5 text-sm text-gray-300 flex-grow mb-6">
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-cyan-400 mt-0.5 flex-shrink-0" /> Unlimited analyses</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-cyan-400 mt-0.5 flex-shrink-0" /> Counterfactual capital tracking</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-cyan-400 mt-0.5 flex-shrink-0" /> Trader Discipline Index (TDI)</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-cyan-400 mt-0.5 flex-shrink-0" /> Unlimited multi-chart review</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-cyan-400 mt-0.5 flex-shrink-0" /> Auto-saved journal + history</li>
              </ul>
              <Link to="/register?plan=pro"
                className="inline-flex justify-center items-center gap-2 px-4 py-3 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black hover:scale-105 transition text-sm font-bold shadow-lg shadow-cyan-500/30">
                Start 30-day trial <SafeIcon icon={FiArrowRight} />
              </Link>
            </div>

            <div className="col-span-12 md:col-span-4 rounded-3xl border border-purple-500/30 bg-gradient-to-b from-purple-900/15 to-gray-900/60 p-8 flex flex-col">
              <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-purple-400 mb-3">For prop firms</span>
              <h3 className="text-2xl font-bold text-white mb-4">Institutional</h3>
              <div className="flex items-baseline gap-1 mb-6">
                <span className="text-5xl font-black font-mono text-purple-400">$149</span>
                <span className="text-gray-500 text-sm">/month</span>
              </div>
              <ul className="space-y-2.5 text-sm text-gray-400 flex-grow mb-6">
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-purple-400 mt-0.5 flex-shrink-0" /> Everything in Pro</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-purple-400 mt-0.5 flex-shrink-0" /> Institutional Risk Officer mode</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-purple-400 mt-0.5 flex-shrink-0" /> Impulse trader prevention</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-purple-400 mt-0.5 flex-shrink-0" /> Capital protection rules</li>
                <li className="flex gap-2"><SafeIcon icon={FiCheck} className="text-purple-400 mt-0.5 flex-shrink-0" /> Priority support</li>
              </ul>
              <Link to="/register?plan=institutional"
                className="inline-flex justify-center px-4 py-3 rounded-xl border border-purple-400/40 text-purple-300 hover:bg-purple-400/10 transition text-sm font-medium">
                Try Institutional
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* ═══════════ FINAL CTA ═══════════ */}
      <section className="relative py-10 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="relative rounded-3xl border border-purple-500/30 bg-gradient-to-br from-purple-900/30 via-gray-900/80 to-cyan-900/30 p-10 md:p-16 overflow-hidden">
            <div className="absolute -top-40 -right-40 w-[500px] h-[500px] bg-cyan-500/20 rounded-full blur-3xl" />
            <div className="absolute -bottom-40 -left-40 w-[500px] h-[500px] bg-purple-500/20 rounded-full blur-3xl" />

            <div className="relative text-center">
              <h2 className="text-4xl md:text-6xl lg:text-7xl font-black mb-6 leading-tight">
                Anyone can take a trade.
                <br />
                <span className="bg-gradient-to-r from-purple-400 via-cyan-300 to-cyan-400 bg-clip-text text-transparent">
                  Pros know when not to.
                </span>
              </h2>
              <p className="text-lg text-gray-300 mb-10 max-w-2xl mx-auto">
                Stop relying on willpower. Let an AI veto the trades that would destroy your account.
              </p>
              <div className="flex flex-col sm:flex-row items-stretch sm:items-center justify-center gap-3 max-w-md mx-auto">
                <Link to="/register"
                  className="inline-flex items-center justify-center gap-3 px-8 py-5 rounded-xl bg-gradient-to-r from-cyan-400 to-purple-400 text-black font-bold text-lg hover:scale-105 transition shadow-2xl shadow-cyan-500/30">
                  Start your free trial <SafeIcon icon={FiArrowRight} />
                </Link>
              </div>
              <p className="mt-4 text-xs text-gray-500 font-mono uppercase tracking-wider">
                30 days · No card · Cancel anytime
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Footer global rendered by App.jsx — no duplicate here */}
    </div>
  );
};

export default LandingPage;
